import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Send, Search, Users, Package, Server, CheckCircle2, ShieldCheck, ImagePlus, X, History } from 'lucide-react';

const PROVIDERS = {
  gmail: { label: 'Gmail', host: 'smtp.gmail.com', port: 587, security: 'starttls' },
  outlook: { label: 'Outlook / Hotmail', host: 'smtp-mail.outlook.com', port: 587, security: 'starttls' },
  office365: { label: 'Microsoft 365', host: 'smtp.office365.com', port: 587, security: 'starttls' },
  custom: { label: 'Otro (personalizado)', host: '', port: 587, security: 'starttls' },
};
const detectProvider = (host) => {
  const h = (host || '').toLowerCase();
  if (h.includes('gmail')) return 'gmail';
  if (h.includes('outlook')) return 'outlook';
  if (h.includes('office365')) return 'office365';
  return 'custom';
};
const emptySmtp = { provider: 'gmail', host: 'smtp.gmail.com', port: 587, security: 'starttls', username: '', password: '', from_email: '', from_name: '' };

export default function Marketing() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [selProducts, setSelProducts] = useState([]);
  const [audience, setAudience] = useState('all'); // 'all' | 'selected'
  const [selClients, setSelClients] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [pq, setPq] = useState('');
  const [sending, setSending] = useState(false);

  // Configuración SMTP por empresa
  const [smtp, setSmtp] = useState(emptySmtp);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  // Imagen del correo + historial de campañas
  const [imgUrl, setImgUrl] = useState('');
  const [uploadingImg, setUploadingImg] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => { (async () => {
    try { const r = await api.getCompanies(); setCompanies(r.data || []); if (r.data?.[0]) setCompanyId(r.data[0].company_id); } catch { /* noop */ }
  })(); }, []);

  useEffect(() => { if (!companyId) return; (async () => {
    try {
      const r = await api.getCompanySmtp(companyId);
      if (r.data?.configured) {
        setSmtpConfigured(true);
        setSmtp({ provider: detectProvider(r.data.host), host: r.data.host, port: r.data.port, security: r.data.security || 'starttls', username: r.data.username || '', password: '', from_email: r.data.from_email || '', from_name: r.data.from_name || '' });
      } else {
        setSmtpConfigured(false); setSmtp(emptySmtp);
      }
    } catch { /* noop */ }
  })(); }, [companyId]);

  const onProvider = (p) => setSmtp(s => ({ ...s, provider: p, ...(p !== 'custom' ? { host: PROVIDERS[p].host, port: PROVIDERS[p].port, security: PROVIDERS[p].security } : {}) }));
  const onFromEmail = (v) => setSmtp(s => ({ ...s, from_email: v, username: (s.provider !== 'custom' || !s.username) ? v : s.username }));

  const saveSmtp = async () => {
    if (!smtp.from_email.trim() || !smtp.username.trim() || !smtp.host.trim()) return toast.error('Completa correo, usuario y servidor');
    if (!smtpConfigured && !smtp.password.trim()) return toast.error('Ingresa la contraseña o clave de aplicación');
    setSmtpSaving(true);
    try {
      const payload = { host: smtp.host.trim(), port: Number(smtp.port), username: smtp.username.trim(), security: smtp.security, from_email: smtp.from_email.trim(), from_name: smtp.from_name.trim() };
      if (smtp.password.trim()) payload.password = smtp.password;
      await api.saveCompanySmtp(companyId, payload);
      setSmtpConfigured(true); setSmtp(s => ({ ...s, password: '' }));
      toast.success('Correo de la empresa guardado ✅');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar el correo'); }
    finally { setSmtpSaving(false); }
  };

  const doTest = async () => {
    if (!testEmail.trim()) return toast.error('Ingresa un correo para la prueba');
    setTesting(true);
    try { await api.testCompanySmtp(companyId, testEmail.trim()); toast.success('Correo de prueba enviado ✅ Revisa tu bandeja'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al enviar la prueba'); }
    finally { setTesting(false); }
  };


  useEffect(() => { if (!companyId) return; (async () => {
    try {
      const [c, p] = await Promise.all([api.getClients({ company_id: companyId }), api.getProducts({ company_id: companyId })]);
      setClients(c.data || []); setProducts(p.data || []); setSelProducts([]); setSelClients([]);
    } catch { toast.error('SalesPRO: Error al cargar datos'); }
  })(); }, [companyId]);

  const loadHistory = async () => {
    if (!companyId) return;
    try { const r = await api.getCampaignHistory(companyId); setHistory(r.data || []); } catch { /* noop */ }
  };
  useEffect(() => {
    loadHistory();
    setImgUrl('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Selecciona una imagen');
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.uploadFile(fd, 'marketing');
      setImgUrl(api.publicFileUrl(r.data.file_id));
      toast.success('Imagen cargada ✅');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al subir imagen'); }
    finally { setUploadingImg(false); }
  };

  const withEmail = clients.filter(c => c.email);
  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);
  const shownProducts = products.filter(p => !pq.trim() || (p.name || '').toLowerCase().includes(pq.toLowerCase()) || (p.code || '').toLowerCase().includes(pq.toLowerCase()));

  const send = async () => {
    if (!subject.trim() || !message.trim()) return toast.error('Completa asunto y mensaje');
    const recipients = audience === 'all' ? [] : selClients;
    if (audience === 'selected' && recipients.length === 0) return toast.error('Selecciona al menos un cliente');
    setSending(true);
    try {
      const res = await api.sendMarketing({ company_id: companyId, client_ids: recipients, product_ids: selProducts, subject, message, image_url: imgUrl || undefined });
      toast.success(`Campaña enviada: ${res.data.sent} enviados, ${res.data.errors} con error (de ${res.data.total})`);
      setSubject(''); setMessage(''); setSelProducts([]); setImgUrl('');
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error al enviar'); }
    finally { setSending(false); }
  };

  const inputCls = 'w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm';

  return (
    <div className="space-y-6 max-w-5xl" data-testid="marketing-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22] flex items-center gap-2"><Mail /> Email Marketing</h1>
        <p className="text-sm text-[#64748B] mt-1">Envía ofertas con productos seleccionados a los clientes de cada empresa</p>
      </div>

      <Card className="border border-[#E2E8F0]" data-testid="smtp-config-card"><CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Server size={18} className="text-[#022C22]" />
            <div>
              <h2 className="font-semibold text-[#022C22]">Correo de la empresa (SMTP)</h2>
              <p className="text-[11px] text-[#94A3B8]">Conecta el Gmail / Hotmail / Outlook de <b>{companies.find(c => c.company_id === companyId)?.name || 'la empresa'}</b> para enviar desde su propia cuenta</p>
            </div>
          </div>
          {smtpConfigured
            ? <span className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2.5 py-1 rounded-full" data-testid="smtp-status"><CheckCircle2 size={13} /> Configurado</span>
            : <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full" data-testid="smtp-status">Sin configurar</span>}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Proveedor</Label>
            <select value={smtp.provider} onChange={e => onProvider(e.target.value)} className={inputCls} data-testid="smtp-provider">
              {Object.entries(PROVIDERS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Nombre del remitente</Label>
            <Input value={smtp.from_name} onChange={e => setSmtp(s => ({ ...s, from_name: e.target.value }))} placeholder="Ej: FyA Multiservicios" data-testid="smtp-from-name" />
          </div>
          <div>
            <Label>Correo (remitente)</Label>
            <Input type="email" value={smtp.from_email} onChange={e => onFromEmail(e.target.value)} placeholder="tucorreo@gmail.com" data-testid="smtp-from-email" />
          </div>
          <div>
            <Label>Usuario (login)</Label>
            <Input type="email" value={smtp.username} onChange={e => setSmtp(s => ({ ...s, username: e.target.value }))} placeholder="normalmente el mismo correo" data-testid="smtp-username" />
          </div>
          <div>
            <Label>Contraseña / Clave de aplicación</Label>
            <Input type="password" value={smtp.password} onChange={e => setSmtp(s => ({ ...s, password: e.target.value }))} placeholder={smtpConfigured ? '•••••••• (guardada, deja vacío para no cambiar)' : 'clave de aplicación'} data-testid="smtp-password" />
          </div>
          {smtp.provider === 'custom' ? (
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Label>Servidor</Label><Input value={smtp.host} onChange={e => setSmtp(s => ({ ...s, host: e.target.value }))} placeholder="smtp.dominio.com" data-testid="smtp-host" /></div>
              <div><Label>Puerto</Label><Input type="number" value={smtp.port} onChange={e => setSmtp(s => ({ ...s, port: e.target.value, security: Number(e.target.value) === 465 ? 'ssl' : 'starttls' }))} data-testid="smtp-port" /></div>
            </div>
          ) : (
            <div className="flex items-end text-xs text-[#94A3B8]">Servidor: {smtp.host}:{smtp.port} · {smtp.security.toUpperCase()}</div>
          )}
        </div>

        <div className="text-[11px] text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-2 flex items-start gap-2">
          <ShieldCheck size={14} className="text-[#022C22] mt-0.5 shrink-0" />
          <span>Para <b>Gmail</b> activa la Verificación en 2 pasos y crea una <b>Contraseña de aplicación</b> (no uses tu contraseña normal). La clave se guarda cifrada.</span>
        </div>

        <div className="flex flex-wrap gap-2 items-end">
          <Button onClick={saveSmtp} disabled={smtpSaving} data-testid="smtp-save"><Server size={15} className="mr-1" /> {smtpSaving ? 'Guardando…' : 'Guardar correo'}</Button>
          <div className="flex items-end gap-2">
            <div><Label className="text-[11px]">Enviar prueba a</Label><Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="tucorreo@ejemplo.com" className="h-9" data-testid="smtp-test-email" /></div>
            <Button variant="outline" onClick={doTest} disabled={testing || !smtpConfigured} data-testid="smtp-test-btn"><Send size={14} className="mr-1" /> {testing ? 'Enviando…' : 'Probar'}</Button>
          </div>
        </div>
      </CardContent></Card>

      <Card className="border border-[#E2E8F0]"><CardContent className="p-4 space-y-4">
        <div>
          <Label>Empresa</Label>
          <select value={companyId} onChange={e => setCompanyId(e.target.value)} className={inputCls} data-testid="marketing-company">
            {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
          </select>
          <p className="text-[11px] text-[#94A3B8] mt-1">{withEmail.length} de {clients.length} clientes tienen email</p>
        </div>

        <div>
          <Label className="flex items-center gap-1"><Users size={14} /> Audiencia</Label>
          <div className="flex gap-4 mt-1 text-sm">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="marketing-aud-all"><input type="radio" checked={audience === 'all'} onChange={() => setAudience('all')} /> Todos con email ({withEmail.length})</label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="marketing-aud-sel"><input type="radio" checked={audience === 'selected'} onChange={() => setAudience('selected')} /> Seleccionar clientes</label>
          </div>
          {audience === 'selected' && (
            <div className="mt-2 max-h-40 overflow-y-auto border border-[#E2E8F0] rounded-md p-2 grid sm:grid-cols-2 gap-1" data-testid="marketing-clients">
              {withEmail.length === 0 && <p className="text-sm text-[#94A3B8]">Sin clientes con email</p>}
              {withEmail.map(c => (
                <label key={c.client_id} className="flex items-center gap-2 text-sm cursor-pointer truncate">
                  <input type="checkbox" checked={selClients.includes(c.client_id)} onChange={() => toggle(selClients, setSelClients, c.client_id)} /> {c.name} <span className="text-[#94A3B8] text-xs truncate">{c.email}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div>
          <Label className="flex items-center gap-1"><Package size={14} /> Productos en la oferta ({selProducts.length})</Label>
          <div className="relative mt-1">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input value={pq} onChange={e => setPq(e.target.value)} placeholder="Buscar producto…" className={inputCls + ' pl-8'} data-testid="marketing-product-search" />
          </div>
          <div className="mt-2 max-h-52 overflow-y-auto border border-[#E2E8F0] rounded-md divide-y divide-[#F1F5F9]" data-testid="marketing-products">
            {shownProducts.slice(0, 60).map(p => (
              <label key={p.product_id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F8FAFC]" data-testid={`marketing-product-${p.product_id}`}>
                <input type="checkbox" checked={selProducts.includes(p.product_id)} onChange={() => toggle(selProducts, setSelProducts, p.product_id)} />
                <span className="flex-1 text-sm truncate">{p.code ? `[${p.code}] ` : ''}{p.name}</span>
                <span className="text-sm font-semibold text-primary">${(p.price || 0).toFixed(2)}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label className="flex items-center gap-1"><ImagePlus size={14} /> Imagen del correo (banner, opcional)</Label>
          {imgUrl ? (
            <div className="mt-1 relative inline-block">
              <img src={imgUrl} alt="banner" className="h-28 rounded-lg border border-[#E2E8F0] object-cover" data-testid="marketing-image-preview" />
              <button onClick={() => setImgUrl('')} className="absolute -top-2 -right-2 bg-[#DC2626] text-white rounded-full p-1" data-testid="marketing-image-remove"><X size={13} /></button>
            </div>
          ) : (
            <label className="mt-1 flex items-center justify-center gap-2 border-2 border-dashed border-[#E2E8F0] rounded-lg h-24 cursor-pointer hover:border-[#022C22] text-sm text-[#64748B]" data-testid="marketing-image-drop">
              <ImagePlus size={18} /> {uploadingImg ? 'Subiendo…' : 'Subir imagen para la campaña'}
              <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploadingImg} data-testid="marketing-image-input" />
            </label>
          )}
        </div>

        <div>
          <Label>Asunto</Label>
          <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="¡Ofertas de la semana! 🔥" data-testid="marketing-subject" />
        </div>
        <div>
          <Label>Mensaje</Label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5} className={inputCls} placeholder="Escribe tu mensaje de oferta…" data-testid="marketing-message" />
        </div>

        <Button onClick={send} disabled={sending} className="w-full sm:w-auto" data-testid="marketing-send">
          <Send size={16} className="mr-1" /> {sending ? 'Enviando…' : 'Enviar campaña'}
        </Button>
      </CardContent></Card>

      <Card className="border border-[#E2E8F0]" data-testid="marketing-history-card"><CardContent className="p-4 space-y-3">
        <h2 className="font-semibold text-[#022C22] flex items-center gap-2"><History size={17} /> Historial de campañas</h2>
        {history.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">Aún no se han enviado campañas para esta empresa</p>
        ) : (
          <div className="divide-y divide-[#F1F5F9]">
            {history.map(h => (
              <div key={h.campaign_id} className="py-2.5 flex items-start gap-3" data-testid="campaign-item">
                {h.image_url && <img src={h.image_url} alt="" className="w-12 h-12 rounded object-cover border border-[#E2E8F0]" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-[#1E293B] truncate">{h.subject}</div>
                  <div className="text-xs text-[#64748B] truncate">{h.message}</div>
                  <div className="text-[11px] text-[#94A3B8] mt-0.5">{new Date(h.created_at).toLocaleString('es-VE')} · vía {h.via?.toUpperCase()} · por {h.sent_by_name || '—'}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-green-700">{h.sent} enviados</div>
                  {h.errors > 0 && <div className="text-xs text-[#DC2626]">{h.errors} con error</div>}
                  <div className="text-[11px] text-[#94A3B8]">de {h.recipients_total}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>
    </div>
  );
}
